<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
  <!-- BUILDING & STRUCTURE -->
  <div class="bg-gray-1 p-4 rounded-xl border border-gray-6">
    <div class="flex items-center gap-2 mb-3 text-gray-11">
      <mat-icon class="text-sm">foundation</mat-icon>
      <h4 class="font-bold uppercase tracking-wider text-xs m-0">Bâtiment & Structure</h4>
    </div>
    <div class="space-y-1.5 text-sm">
      @if (construction().year) {
        <p class="text-gray-11 flex justify-between">
          <span class="font-medium text-gray-12">Période:</span>
          <span>{{ construction().year | etxeEnumDisplay: 'constructionPeriod' }}</span>
        </p>
      }
      @if (construction().standing) {
        <p class="text-gray-11 flex justify-between">
          <span class="font-medium text-gray-12">Standing:</span>
          <span>{{ construction().standing | etxeEnumDisplay: 'standing' }}</span>
        </p>
      }
      @if (construction().material?.walls && construction().material?.walls?.length) {
        <div class="text-gray-11">
          <span class="font-medium text-gray-12 block mb-1">Murs:</span>
          <div class="flex flex-wrap gap-1">
            @for (mat of construction().material?.walls; track mat) {
              <span class="px-1.5 py-0.5 rounded bg-gray-1 border border-gray-6 text-[10px]">{{
                mat | etxeEnumDisplay: 'material'
              }}</span>
            }
          </div>
        </div>
      }
      @if (construction().material?.roof && construction().material?.roof?.length) {
        <div class="text-gray-11">
          <span class="font-medium text-gray-12 block mb-1">Toiture:</span>
          <div class="flex flex-wrap gap-1">
            @for (mat of construction().material?.roof; track mat) {
              <span class="px-1.5 py-0.5 rounded bg-gray-1 border border-gray-6 text-[10px]">{{
                mat | etxeEnumDisplay: 'roofMaterial'
              }}</span>
            }
          </div>
        </div>
      }
      @if (construction().buildingHeight) {
        <p class="text-gray-11 flex justify-between">
          <span class="font-medium text-gray-12">Hauteur:</span>
          <span>{{ construction().buildingHeight }} m</span>
        </p>
      }
      @if (construction().ceilingHeight) {
        <p class="text-gray-11 flex justify-between">
          <span class="font-medium text-gray-12">Hauteur sous plafond:</span>
          <span>{{ construction().ceilingHeight }} m</span>
        </p>
      }
      @if (construction().groundFootprint) {
        <p class="text-gray-11 flex justify-between">
          <span class="font-medium text-gray-12">Emprise au sol:</span>
          <span>{{ construction().groundFootprint }} m²</span>
        </p>
      }
      @if (construction().openings) {
        <p class="text-gray-11 flex justify-between">
          <span class="font-medium text-gray-12">Nb ouvertures:</span>
          <span>{{ construction().openings }}</span>
        </p>
      }
    </div>
  </div>

  <!-- PROPERTY STATUS -->
  <div class="bg-gray-1 p-4 rounded-xl border border-gray-6">
    <div class="flex items-center gap-2 mb-3 text-gray-11">
      <mat-icon class="text-sm">analytics</mat-icon>
      <h4 class="font-bold uppercase tracking-wider text-xs m-0">État du bien</h4>
    </div>
    <div class="space-y-1.5 text-sm">
      @if (construction().conditions?.interior) {
        <p class="text-gray-11 flex justify-between">
          <span class="font-medium text-gray-12">État intérieur:</span>
          <span>{{
            $safeNavigationMigration(construction().conditions?.interior) | etxeEnumDisplay: 'condition'
          }}</span>
        </p>
      }
      @if (!isHouse() && construction().conditions?.commonAreas) {
        <p class="text-gray-11 flex justify-between">
          <span class="font-medium text-gray-12">Parties communes:</span>
          <span>{{
            $safeNavigationMigration(construction().conditions?.commonAreas) | etxeEnumDisplay: 'condition'
          }}</span>
        </p>
      }
      @if (!construction().conditions?.interior && (isHouse() || !construction().conditions?.commonAreas)) {
        <p class="text-gray-10 italic">Aucune information sur l'état</p>
      }
    </div>
  </div>

  <!-- ENERGY & COMFORT -->
  <div class="bg-gray-1 p-4 rounded-xl border border-gray-6">
    <div class="flex items-center gap-2 mb-3 text-gray-11">
      <mat-icon class="text-sm">wb_sunny</mat-icon>
      <h4 class="font-bold uppercase tracking-wider text-xs m-0">Cadre & Exposition</h4>
    </div>
    <div class="space-y-2 text-sm">
      @if (construction().insulation) {
        <div>
          <span class="font-medium text-gray-12 block mb-1">Isolation:</span>
          <div class="grid grid-cols-2 gap-1 text-xs text-gray-11">
            @if (construction().insulation?.walls) {
              <div>
                Murs:
                <span class="capitalize font-medium text-gray-12">{{
                  construction().insulation?.walls
                }}</span>
              </div>
            }
            @if (construction().insulation?.glazing) {
              <div>
                Vitrages:
                <span class="capitalize font-medium text-gray-12">{{
                  construction().insulation?.glazing
                }}</span>
              </div>
            }
            @if (construction().insulation?.floor) {
              <div>
                Planchers:
                <span class="capitalize font-medium text-gray-12">{{
                  construction().insulation?.floor
                }}</span>
              </div>
            }
            @if (construction().insulation?.envelope) {
              <div>
                Enveloppe:
                <span class="capitalize font-medium text-gray-12">{{
                  construction().insulation?.envelope
                }}</span>
              </div>
            }
          </div>
        </div>
      }
      @if (construction().orientation && construction().orientation?.length) {
        <div>
          <span class="font-medium text-gray-12 block">Orientation</span>
          @for (ori of construction().orientation; track ori; let last = $last) {
            <span class="text-gray-11 uppercase text-[10px] font-bold"
              >{{ ori | etxeEnumDisplay: 'orientation' }}{{ last ? '' : ', ' }}</span
            >
          }
        </div>
      }
      @if (construction().exposure) {
        <div>
          <span class="font-medium text-gray-12 block">Exposition</span>
          <span class="text-gray-11">{{ construction().exposure | etxeEnumDisplay: 'exposure' }}</span>
        </div>
      }
      @if (viewsList().length > 0) {
        <div class="col-span-2">
          <span class="font-medium text-gray-12 block">Vue(s)</span>
          @for (v of viewsList(); track v; let last = $last) {
            <span class="text-gray-11">{{ v | etxeEnumDisplay: 'view' }}{{ last ? '' : ', ' }}</span>
          }
        </div>
      }
    </div>
  </div>
</div>
